import React, { useEffect, useState, useMemo, useCallback, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import {
  Card, Table, Button, Space, Tag, Typography, Pagination,
  Form, Row, Col, Input, Select, message, Tooltip, Modal, Switch, Checkbox, Divider, Image
} from 'antd';
import {
  DownloadOutlined, SearchOutlined, ReloadOutlined,
  EyeOutlined, EditOutlined, InfoCircleOutlined, SortAscendingOutlined, TagsOutlined
} from '@ant-design/icons';
import { InventoryUnitLabelAdapter } from '../../project/inventory-config/labels/adapters/inventoryLabelAdapter';
import { inventoryLabelService } from '../../project/inventory-config/labels/services/inventoryLabelService';
import { useTranslation } from 'react-i18next';
import { useTableQuery } from '../../../hooks/useTableQuery';
import { getCustomerCurrentInventory, getInventoryUnitDetail, downloadInventoryRecords } from '../../../services/inventory';
import { getAttachmentUrl } from '../../../utils/attachmentUrl';
import { getAllWarehouses } from '../../../services/warehouse';
import { getCurrentUserProjects, getCustomerList } from '../../../services/project';
import type { CustomerInventoryItem, InventoryUnit, InventoryFilterParams } from '../../../types/inventory';
import type { WarehouseDetail } from '../../../types/warehouse';
import type { Project } from '../../../types/project';
import dayjs from 'dayjs';
import { useAuth } from '../../../hooks/useAuth';
import { usePermissions } from '../../../contexts/PermissionContext';

const { Title, Text } = Typography;
const { Option } = Select;

// 客户类型定义 - 与产品列表页保持一致
interface Customer {
  id: string;
  username: string;
}

const CurrentInventory: React.FC = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const { t } = useTranslation();
  const { user } = useAuth(); // 使用useAuth获取用户信息
  const { hiddenFields, exportHiddenFields, isWarehouseStaff, isWarehouseSupervisor, permissions } = usePermissions();
  const [form] = Form.useForm();
  const [expandedRows, setExpandedRows] = useState<string[]>([]);
  const [selectedItems, setSelectedItems] = useState<string[]>([]);
  const [loading, setLoading] = useState(false);
  const [warehouses, setWarehouses] = useState<WarehouseDetail[]>([]);
  const [projects, setProjects] = useState<Project[]>([]);
  const [projectsLoading, setProjectsLoading] = useState(false);
  const [customers, setCustomers] = useState<Customer[]>([]);
  const [customersLoading, setCustomersLoading] = useState(false);
  const [contentDetailsVisible, setContentDetailsVisible] = useState(false);
  const [contentDetailsData, setContentDetailsData] = useState<any[]>([]);
  const [contentDetailsUnitCode, setContentDetailsUnitCode] = useState<string>('');
  const [contentDetailsUnitTypeText, setContentDetailsUnitTypeText] = useState<string>('');
  // 敏感字段策略：根据策略决定是否隐藏“客户”筛选框
  const _hidden = hiddenFields('inventory.InventoryUnit');
  const hideCustomerByStrategy = Array.isArray(_hidden) && _hidden.includes('sorting_group__project_inventory_config__project__customer__username');


  // 权限矩阵：仓库员工/主管 -> 仅显示被分配的仓库，并默认选定
  const assignedWarehouseIds = (permissions?.assigned_warehouse_ids || []) as number[];
  const restrictedByWarehouseRole = (isWarehouseStaff && isWarehouseStaff()) || (isWarehouseSupervisor && isWarehouseSupervisor());
  const warehousesForFilter = useMemo(() => {
    if (!restrictedByWarehouseRole) return warehouses;
    const idSet = new Set(assignedWarehouseIds || []);
    return warehouses.filter(w => idSet.has(w.id));
  }, [restrictedByWarehouseRole, warehouses, assignedWarehouseIds]);


  const [downloading, setDownloading] = useState(false);
  const [downloadModalVisible, setDownloadModalVisible] = useState(false);
  // 多级排序
  const [sortModalVisible, setSortModalVisible] = useState(false);
  const [sortRules, setSortRules] = useState<Array<{ field: string; order: 'asc' | 'desc' }>>([]);
  const [labelPreviewVisible, setLabelPreviewVisible] = useState(false);
  const [labelPreviewUrl, setLabelPreviewUrl] = useState<string | null>(null);
  const [labelPreviewIsPDF, setLabelPreviewIsPDF] = useState<boolean>(false);
  const [labelPreviewRecord, setLabelPreviewRecord] = useState<CustomerInventoryItem | null>(null);
  const [contentDetailsCopied, setContentDetailsCopied] = useState(false);
  const [copiedWeightDimKey, setCopiedWeightDimKey] = useState<string | null>(null);
  const [copiedAgeVolumeKey, setCopiedAgeVolumeKey] = useState<string | null>(null);
  const [stockTotals, setStockTotals] = useState<{ onHand: number; reserved: number; available: number; totalVolumeM3: number }>({
    onHand: 0,
    reserved: 0,
    available: 0,
    totalVolumeM3: 0,
  });
  const [stockTotalsLoading, setStockTotalsLoading] = useState(false);
  const projectJumpHandledRef = useRef<string>('');

  const getAgeBadgeColor = useCallback((age?: number | null) => {
    if (typeof age !== 'number' || isNaN(age)) return 'default';
    if (age <= 30) return 'default';
    if (age <= 60) return 'blue';
    if (age <= 90) return 'orange';
    return 'red';
  }, []);

  // 可排序字段与显示名称（与客户页保持一致）
  const sortFieldOptions = useMemo(() => (
    [
      { label: '加权库龄', value: 'age_avg_days' },
      { label: '总库容体积', value: 'calc_total_volume' },
      { label: '可用数量', value: 'warehouse_total_quantity' }
    ]
  ), []);

  const getFieldLabel = useCallback((value: string) => {
    const item = sortFieldOptions.find(i => i.value === value);
    return item ? item.label : value;
  }, [sortFieldOptions]);

  const formatDimValue = useCallback((v: any) => {
    if (v == null || v === '') return '-';
    return `${v} cm`;
  }, []);

  const formatWeightValue = useCallback((v: any) => {
    if (v == null || v === '') return '-';
    return `${v} g`;
  }, []);

  const copyText = useCallback(async (text: string) => {
    try {
      if (navigator?.clipboard?.writeText) {
        await navigator.clipboard.writeText(text);
        message.success('已复制');
        return true;
      }
    } catch (err) {
      console.warn('clipboard write failed:', err);
    }
    try {
      const ta = document.createElement('textarea');
      ta.value = text;
      ta.style.position = 'fixed';
      ta.style.left = '-9999px';
      document.body.appendChild(ta);
      ta.select();
      document.execCommand('copy');
      document.body.removeChild(ta);
      message.success('已复制');
      return true;
    } catch {
      message.error('复制失败');
      return false;
    }
  }, []);

  const buildWeightDimCopyText = useCallback((record: CustomerInventoryItem) => {
    const confirmed = !!(record as any)?.weight_dimensions_confirmed;
    const code = (record as any)?.system_barcode || (record as any)?.id || '-';
    const lines = [
      `库存单元编码: ${code}`,
      `重量尺寸是否已确认: ${confirmed ? t('common.confirmed') : t('common.unconfirmed')}`,
    ];
    if (confirmed) {
      lines.push(
        `实际长度: ${formatDimValue((record as any)?.actual_length)}`,
        `实际宽度: ${formatDimValue((record as any)?.actual_width)}`,
        `实际高度: ${formatDimValue((record as any)?.actual_height)}`,
        `实际重量: ${formatWeightValue((record as any)?.actual_weight)}`
      );
    } else {
      lines.push(
        `声明长度: ${formatDimValue((record as any)?.length)}`,
        `声明宽度: ${formatDimValue((record as any)?.width)}`,
        `声明高度: ${formatDimValue((record as any)?.height)}`,
        `声明重量: ${formatWeightValue((record as any)?.weight)}`
      );
    }
    return lines.join('\n');
  }, [formatDimValue, formatWeightValue, t]);

  const buildAgeVolumeCopyText = useCallback((record: CustomerInventoryItem) => {
    const code = (record as any)?.system_barcode || (record as any)?.id || '-';
    const buckets = (record as any)?.age_bucket_volumes || null;
    const lines = [`库存单元编码: ${code}`];
    if (!buckets) {
      lines.push('无年龄桶体积分布数据');
      return lines.join('\n');
    }
    const ordered = ['0-30', '31-60', '61-90', '90+'];
    ordered.forEach((k) => {
      const v = (buckets as any)[k];
      lines.push(`${k} 天: ${typeof v === 'number' ? `${Number(v).toFixed(3)} m³` : '-'}`);
    });
    return lines.join('\n');
  }, []);

  const buildContentDetailsCopyText = useCallback((items: any[]) => {
    if (!Array.isArray(items) || items.length === 0) return '无内容项';
    return items
      .map((item, idx) => {
        const lines: string[] = [];
        lines.push(`内容项 ${idx + 1}`);
        lines.push(`产品名称: ${item?.product_name ?? '-'}`);
        lines.push(`数量: ${item?.quantity ?? '-'}`);
        lines.push(`客户自定义编码: ${item?.product_code ?? '-'}`);
        lines.push(`系统编码: ${item?.system_code ?? '-'}`);
        if (Array.isArray(item?.sorting_codes) && item.sorting_codes.length > 0) {
          const codes = item.sorting_codes
            .map((c: any) => (c?.description ? `${c.code}(${c.description})` : c?.code || '-'))
            .join(', ');
          lines.push(`分拣条码: ${codes}`);
        } else {
          lines.push('分拣条码: -');
        }
        return lines.join('\n');
      })
      .join('\n\n');
  }, []);

  // 判断当前角色
  const isAdmin = location.pathname.startsWith('/dashboard/admin');
  const isStaff = location.pathname.startsWith('/dashboard/staff');
  const role = isAdmin ? 'admin' : isStaff ? 'staff' : 'customer';

  // 使用真实的API获取数据
  const { state, data, actions, paginationConfig } = useTableQuery({
    initialState: {
      pagination: { current: 1, pageSize: 20, total: 0 },
      filters: {
        customer: '',
        search: '',
        remarks: '',
        warehouse: '',
        unit_type: '',
        project: '',
        system_barcode: '',
        sorting_barcode: '',
        source: '',
        weight_dimensions_confirmed: '',
        has_logistics_package: '',
        hide_zero_stock: false,
        ordering: ''
      },
      sorting: { field: 'total_quantity', order: 'desc' }
    },
    urlConfig: {
      syncToUrl: true,
      urlKeys: ['pagination', 'filters', 'sorting']
    },
    fetchData: async (state) => {
      setStockTotalsLoading(true);
      const orderingFromFilters = (state as any).filters?.ordering as string | undefined;
      try {
        const response = await getCustomerCurrentInventory({
          page: state.pagination.current,
          page_size: state.pagination.pageSize,
          search: state.filters.search,
          remarks: state.filters.remarks,
          warehouse: state.filters.warehouse,
          unit_type: state.filters.unit_type,
          project: state.filters.project,
          system_barcode: state.filters.system_barcode,
          sorting_barcode: state.filters.sorting_barcode,
          source: state.filters.source,
          weight_dimensions_confirmed: state.filters.weight_dimensions_confirmed,
          has_logistics_package: state.filters.has_logistics_package,
          hide_zero_stock: state.filters.hide_zero_stock,
          customer: !hideCustomerByStrategy ? state.filters.customer : undefined,
          ordering: (() => {
            if (orderingFromFilters && typeof orderingFromFilters === 'string') {
              console.log('多列排序(来自filters):', orderingFromFilters);
              return orderingFromFilters;
            }
            const ordering = (() => {
              if (state.sorting.field === 'updated_at') {
                return state.sorting.order === 'desc' ? '-updated_at' : 'updated_at';
              } else if (state.sorting.field === 'total_quantity') {
                return state.sorting.order === 'desc' ? '-warehouse_total_quantity' : 'warehouse_total_quantity';
              } else if (state.sorting.field === 'calc_total_volume') {
                return state.sorting.order === 'desc' ? '-calc_total_volume' : 'calc_total_volume';
              }
              return undefined;
            })();
            console.log('单列排序参数:', { field: state.sorting.field, order: state.sorting.order, ordering });
            return ordering;
          })()
        });
        const dataWithSummary = (response?.data || {}) as any;
        const summary = dataWithSummary.summary || {};
        setStockTotals({
          onHand: Number(summary.on_hand_total || 0),
          reserved: Number(summary.reserved_total || 0),
          available: Number(summary.available_total || 0),
          totalVolumeM3: Number(summary.total_volume_m3 || 0),
        });
        return {
          results: response.data.results || [],
          count: response.data.count || 0
        };
      } finally {
        setStockTotalsLoading(false);
      }
    }
  });

  // 特例：仅处理“从项目列表跳转到当前库存”时的项目筛选覆盖，不影响通用 tab 状态恢复逻辑
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const fromProjectList = params.get('from_project_list') === '1';
    const filterProject = String(params.get('filter_project') || '').trim();
    const jumpTs = String(params.get('jump_ts') || '').trim();
    if (!fromProjectList || !filterProject) return;
    const jumpKey = jumpTs || `project:${filterProject}`;
    if (projectJumpHandledRef.current === jumpKey) return;
    projectJumpHandledRef.current = jumpKey;

    actions.updateFilters({ project: filterProject });

    params.delete('from_project_list');
    params.delete('filter_project');
    params.delete('jump_ts');
    const nextSearch = params.toString();
    navigate(`${location.pathname}${nextSearch ? `?${nextSearch}` : ''}`, { replace: true });
  }, [location.pathname, location.search, navigate, actions.updateFilters]);

  // 监听 filters.ordering -> 本地 sortRules（保持与客户页一致）
  useEffect(() => {
    if (sortModalVisible) return;
    const ordering = (state as any)?.filters?.ordering as string | undefined;
    if (!ordering) {
      setSortRules([]);
      return;
    }
    const rules = (ordering
      .split(',')
      .map(k => k.trim())
      .filter(Boolean)
      .map(k => ({ field: k.replace(/^-/, ''), order: (k.startsWith('-') ? 'desc' : 'asc') as 'asc' | 'desc' }))
      .slice(0, 2)) as Array<{ field: string; order: 'asc' | 'desc' }>;
    setSortRules(rules as Array<{ field: string; order: 'asc' | 'desc' }>);
  }, [state.filters?.ordering, sortModalVisible]);

  // 生成 ordering 字符串
  const buildOrdering = useCallback((rules: Array<{ field: string; order: 'asc' | 'desc' }>) => {
    const deduped: Array<{ field: string; order: 'asc' | 'desc' }>= [];
    const seen = new Set<string>();
    rules.forEach(r => {
      if (r.field && !seen.has(r.field)) {
        deduped.push(r);
        seen.add(r.field);
      }
    });
    return deduped.map(r => (r.order === 'desc' ? '-' : '') + r.field).join(',');
  }, []);

  const applyMultiSort = useCallback(() => {
    const validRules = sortRules.filter(r => r.field && (r.order === 'asc' || r.order === 'desc'));
    if (validRules.length === 0) {
      actions.updateFilters({ ordering: '' as any });
      setSortModalVisible(false);
      return;
    }
    const ordering = buildOrdering(validRules);
    actions.updateFilters({ ordering } as any);
    setSortModalVisible(false);
  }, [sortRules, buildOrdering, actions]);

  const resetMultiSort = useCallback(() => {
    setSortRules([]);
    actions.updateFilters({ ordering: '' as any });
  }, [actions]);

  // 管理后台不强依赖内标签，直接使用路由跳转

  // 获取仓库列表
  const fetchWarehouses = useCallback(async () => {
    try {
      const warehousesData = await getAllWarehouses();
      setWarehouses(warehousesData);
    } catch (error) {
      console.error('获取仓库列表失败:', error);
    }
  }, []);

  // 获取项目列表
  const fetchProjects = useCallback(async () => {
    try {
      setProjectsLoading(true);
      const projectsData = await getCurrentUserProjects();
      setProjects(projectsData);
    } catch (error) {
      console.error('获取项目列表失败:', error);
    } finally {
      setProjectsLoading(false);
    }
  }, []);

  // 获取客户列表（仅对员工和管理员显示）
  const fetchCustomers = useCallback(async () => {
    // 只有员工和管理员需要看到客户筛选
    if (user && (user.role === 'admin' || user.role === 'staff') && !hideCustomerByStrategy) {
      setCustomersLoading(true);
      try {
        const customersData = await getCustomerList({ onlyMain: true });
        setCustomers(customersData);
        console.log('✅ 获取客户列表成功:', customersData);
      } catch (error) {
        console.error('❌ 获取客户列表失败:', error);
      } finally {
        setCustomersLoading(false);
      }
    } else {
      setCustomers([]);
      setCustomersLoading(false);
    }
  }, [user, hideCustomerByStrategy]);

  // 组件挂载时获取仓库列表
  useEffect(() => {
    fetchWarehouses();
    fetchProjects();
    fetchCustomers();
  }, [fetchWarehouses, fetchProjects, fetchCustomers]);

  // 当为仓库员工/主管时，若未选择仓库，则默认选择被分配的第一个仓库(code)
  useEffect(() => {
    if (!restrictedByWarehouseRole) return;
    if (!state.filters?.warehouse) {
      if (warehousesForFilter.length > 0) {
        actions.updateFilters({ warehouse: warehousesForFilter[0].code });
      }
    } else {
      // 如果当前选中的仓库不在可选范围内，则重置为第一个
      const exists = warehousesForFilter.some(w => w.code === state.filters.warehouse);
      if (!exists && warehousesForFilter.length > 0) {
        actions.updateFilters({ warehouse: warehousesForFilter[0].code });
      }
    }
  }, [restrictedByWarehouseRole, warehousesForFilter, state.filters?.warehouse, actions]);

  // 当策略隐藏客户信息时，清理筛选中的客户参数，避免无效参数外传
  useEffect(() => {
    if (hideCustomerByStrategy && state.filters?.customer) {
      actions.updateFilters({ customer: '' });
    }
  }, [hideCustomerByStrategy, state.filters?.customer, actions]);

  // 处理创建标签页
  const handleCreateTab = (type: string, id?: string) => {
    const basePath = isAdmin ? '/dashboard/admin' : '/dashboard/staff';
    const tabInfo = {
      key: type === 'detail' ? `inventory-detail-${id}` : 'inventory-create',
      title: type === 'detail' ? `${t('tab.inventoryDetail')} - ${id}` : t('tab.inventoryCreate'),
      path: type === 'detail' ? `${basePath}/inventory/detail/${id}` : `${basePath}/inventory/create`,
      type
    };
    navigate(tabInfo.path);
  };

  // 处理查看详情
  const handleViewDetail = async (record: CustomerInventoryItem) => {
    try {
      setLoading(true);
      const detail = await getInventoryUnitDetail(record.id);
      handleCreateTab('detail', record.system_barcode);
    } catch (error) {
      message.error('获取库存详情失败');
      console.error('获取库存详情失败:', error);
    } finally {
      setLoading(false);
    }
  };

  // 处理重置
  const handleReset = async () => {
    form.resetFields();
    await actions.reset();
  };

  // 显示内容项详细信息
  const showContentDetails = (contentItems: any[], unitCode?: string, unitTypeText?: string) => {
    setContentDetailsData(contentItems);
    setContentDetailsUnitCode(String(unitCode || '').trim());
    setContentDetailsUnitTypeText(String(unitTypeText || '').trim());
    setContentDetailsVisible(true);
  };

  // 处理批量下载
  const handleExport = () => {
    // 检查是否有数据可下载
    if (state.pagination.total === 0) {
      message.warning('没有数据可下载');
      return;
    }
    setDownloadModalVisible(true);
  };

  // 确认下载（带字段选择）
  const handleConfirmDownload = async (selectedFields: string[]) => {
    console.log('🔍 收到字段选择确认:', selectedFields);
    console.log('🔍 字段数量:', selectedFields.length);
    
    setDownloadModalVisible(false);
    setDownloading(true);
    
    try {
      // 导出敏感策略兜底：过滤被隐藏的字段（如仓库员工隐藏客户）
      const exportHidden = exportHiddenFields('inventory.InventoryUnit');
      const isFieldHiddenForExport = (fieldKey: string): boolean => {
        const mapping: Record<string, string[]> = {
          customer_name: ['sorting_group__project_inventory_config__project__customer__username'],
        };
        const related = mapping[fieldKey] || [];
        if (!Array.isArray(exportHidden) || exportHidden.length === 0) return false;
        return related.some(k => exportHidden.includes(k));
      };
      const filteredSelectedFields = (selectedFields || []).filter(k => !isFieldHiddenForExport(k));
      if (filteredSelectedFields.length === 0) {
        message.warning('根据敏感策略，暂无可导出的字段');
        return;
      }

      // 立即开始下载，使用过滤后的字段
      await handleBatchDownload(filteredSelectedFields);
    } catch (error) {
      console.error('下载失败:', error);
      message.error('下载失败，请重试');
    } finally {
      setDownloading(false);
    }
  };

  // 批量下载函数重构
  const handleBatchDownload = async (selectedFields: string[]) => {
    try {
      console.log('🚀 开始批量下载:', {
        selectedFields,
        fieldsCount: selectedFields.length,
        filters: state.filters,
        timestamp: new Date().toISOString()
      });
      
      if (data.length === 0) {
        message.warning('没有数据可下载');
        return;
      }

      const hide = message.loading('正在下载中...', 0);
      
      // 当策略隐藏客户时，不向导出接口传递客户筛选参数
      const exportFilters = { ...state.filters } as any;
      if (hideCustomerByStrategy && 'customer' in exportFilters) {
        delete exportFilters.customer;
      }

      // 使用前端本地生成CSV文件的方式
      await downloadInventoryRecords({
        filters: exportFilters,
        selected_fields: selectedFields,  // 使用传入的字段
        file_format: 'csv'
      });
      
      hide();
      message.success('库存数据下载成功');
      
    } catch (error) {
      message.error('库存数据下载失败');
      console.error('库存数据下载失败:', error);
    }
  };

  // 获取页面标题
  const getPageTitle = () => {
    switch (role) {
      case 'admin':
        return '管理员 - 当前库存';
      case 'staff':
        return '员工 - 当前库存';
      default:
        return '当前库存';
    }
  };

  return (
    <div className="list-page-container">
      <Card>
        {/* 页面标题和统计信息 */}
        <div style={{ marginBottom: 16 }}>
          <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
              <Title level={4} style={{ margin: 0 }}>
                {getPageTitle()}
              </Title>
              <div className="flex items-center space-x-2">
                <Tag color="blue" style={{ fontSize: '14px', padding: '8px' }}>
                  {t('pagination.totalRecords', { total: state.pagination.total })}
                </Tag>
                {(state.filters as any)?.ordering ? (
                  <Tag color="green" style={{ fontSize: '14px', padding: '8px' }}>
                    排序：{
                      ((state.filters as any).ordering as string).split(',').map((k: string, idx: number) => {
                        const desc = k.startsWith('-');
                        const field = k.replace(/^-/, '');
                        return (
                          <span key={idx}>
                            {idx > 0 ? '，' : ''}{getFieldLabel(field)}{desc ? '↓' : '↑'}
                          </span>
                        );
                      })
                    }
                  </Tag>
                ) : null}
              </div>
            </div>
            <div className="flex items-center space-x-2">
              <Button icon={<SortAscendingOutlined />} onClick={() => setSortModalVisible(true)}>数据排序</Button>
              <Button 
                icon={<DownloadOutlined />} 
                onClick={handleExport}
                disabled={state.pagination.total === 0}
              >
                批量下载({state.pagination.total}条)
              </Button>
            </div>
          </div>
        </div>

        {/* 筛选表单 */}
        <Form
          form={form}
          layout="horizontal"
          style={{ marginBottom: 16 }}
        >
          <Row gutter={16}>
            {/* 客户筛选框 - 仅对员工和管理员显示 且 未被敏感策略隐藏 */}
            {user && (user.role === 'admin' || user.role === 'staff') && !hideCustomerByStrategy && (
              <Col span={4}>
                <Form.Item label="客户">
                  <Select
                    placeholder="请选择客户"
                    allowClear
                    loading={customersLoading}
                    showSearch
                    optionFilterProp="children"
                    value={state.filters.customer || undefined}
                    onChange={(value) => {
                      actions.updateFilters({ customer: value || undefined });
                    }}
                    style={{ width: '100%' }}
                    dropdownStyle={{ maxHeight: 300, minWidth: '320px' }}
                    notFoundContent={
                      <div>
                        <div>暂无客户数据</div>
                        <Button 
                          type="link" 
                          size="small" 
                          onClick={() => {
                            // 重新获取客户列表
                            const fetchCustomers = async () => {
                              setCustomersLoading(true);
                              try {
                                const customersData = await getCustomerList();
                                setCustomers(customersData);
                              } catch (error) {
                                console.error('重新获取客户列表失败:', error);
                              } finally {
                                setCustomersLoading(false);
                              }
                            };
                            fetchCustomers();
                          }}
                          loading={customersLoading}
                        >
                          重新加载
                        </Button>
                      </div>
                    }
                  >
                    {customers.map(customer => (
                      <Option key={customer.id} value={customer.id}>
                        {customer.username}
                      </Option>
                    ))}
                  </Select>
                </Form.Item>
              </Col>
            )}
            <Col span={4}>
              <Form.Item label="搜索所含产品">
                <Input
                  placeholder="模糊匹配产品名称或产品自定义编码"
                  allowClear
                  prefix={<SearchOutlined />}
                  value={state.filters.search || ''}
                  onChange={(e) => {
                    const v = (e.target.value || '').trim();
                    actions.updateFilters({ search: v });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label={t('inventory.sortingBarcode')}>
                <Input 
                  placeholder={t('inventory.searchSortingBarcode')} 
                  allowClear 
                  value={state.filters.sorting_barcode || ''}
                  onChange={(e) => {
                    const v = (e.target.value || '').trim();
                    actions.updateFilters({ sorting_barcode: v });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label={t('inventory.unitType')}>
                <Select
                  placeholder={t('inventory.selectUnitType')}
                  allowClear
                  value={state.filters.unit_type || undefined}
                  onChange={(value) => {
                    actions.updateFilters({ unit_type: value });
                  }}
                >
                  <Option value="LIU">{t('inventory.unitTypeLoose')}</Option>
                  <Option value="BIU">{t('inventory.unitTypeBox')}</Option>
                  <Option value="PIU">{t('inventory.unitTypePallet')}</Option>
                  <Option value="AIU">{t('inventory.unitTypeAbnormal')}</Option>
                  <Option value="NIU">{t('inventory.unitTypeNonProduct')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label={t('inventory.project')}>
                <Select
                  placeholder="选择所属项目 可以多选"
                  allowClear
                  mode="multiple"
                  loading={projectsLoading}
                  showSearch
                  filterOption={(input, option) => {
                    if (!option?.label) return false;
                    const searchText = input.toLowerCase();
                    const optionText = option.label.toString().toLowerCase();
                    return optionText.includes(searchText);
                  }}
                  notFoundContent={projectsLoading ? '加载中...' : projects.length === 0 ? '暂无项目' : '未找到匹配的项目'}
                  maxTagCount={1}
                  maxTagTextLength={80}
                  style={{ width: '100%' }}
                  optionLabelProp="label"
                  value={state.filters.project ? 
                         state.filters.project.split(',').filter(Boolean) : undefined}
                  onChange={(selectedValues) => {
                    const projectValue = selectedValues && selectedValues.length > 0 ? 
                                      selectedValues.join(',') : undefined;
                    actions.updateFilters({ project: projectValue });
                  }}
                >
                  {projects.map(project => (
                    <Option 
                      key={project.id} 
                      value={project.id.toString()}
                      label={`${project.project_code} - ${project.name}`}
                    >
                      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                        <span style={{ fontWeight: 500 }}>{project.project_code}</span>
                        <span style={{ color: '#666', marginLeft: 8 }}>{project.name}</span>
                      </div>
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="系统编码">
                <Input 
                  placeholder={t('inventory.searchSystemBarcode')} 
                  allowClear 
                  value={state.filters.system_barcode || ''}
                  onChange={(e) => {
                    const v = (e.target.value || '').trim();
                    actions.updateFilters({ system_barcode: v });
                  }}
                />
              </Form.Item>
            </Col>
          </Row>
          <Row gutter={16}>
            <Col span={4}>
              <Form.Item label={t('inventory.warehouse')}>
                <Select
                  placeholder={t('inventory.selectWarehouse')}
                  allowClear
                  value={state.filters.warehouse || undefined}
                  onChange={(value) => {
                    actions.updateFilters({ warehouse: value });
                  }}
                >
                  {(warehousesForFilter.length > 0 ? warehousesForFilter : warehouses).map(warehouse => (
                    <Option key={warehouse.id} value={warehouse.code}>
                      {warehouse.name} ({warehouse.code})
                    </Option>
                  ))}
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label={t('inventory.source')}>
                <Select
                  placeholder={t('inventory.selectSource')}
                  allowClear
                  value={state.filters.source || undefined}
                  onChange={(value) => {
                    actions.updateFilters({ source: value });
                  }}
                >
                  <Option value="customer">客户创建</Option>
                  <Option value="warehouse">仓库操作创建</Option>
                  <Option value="other">其他</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label={t('inventory.weightDimensionsConfirmed')}>
                <Select
                  placeholder={t('inventory.selectWeightDimensionsStatus')}
                  allowClear
                  value={state.filters.weight_dimensions_confirmed || undefined}
                  onChange={(value) => {
                    actions.updateFilters({ weight_dimensions_confirmed: value });
                  }}
                >
                  <Option value="true">{t('common.confirmed')}</Option>
                  <Option value="false">{t('common.unconfirmed')}</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={4}>
              <Form.Item label="物流包装">
                <Select
                  placeholder="选择物流包装状态"
                  allowClear
                  style={{ width: '100%' }}
                  dropdownStyle={{ maxHeight: 200 }}
                  value={state.filters.has_logistics_package || undefined}
                  onChange={(value) => {
                    actions.updateFilters({ has_logistics_package: value });
                  }}
                >
                  <Option value="self_contained">自带物流包装</Option>
                  <Option value="no_package">无物流包装</Option>
                  <Option value="not_applicable">不适用</Option>
                </Select>
              </Form.Item>
            </Col>
            <Col span={6}>
              <Form.Item label="备注">
                <Input
                  className="inventory-remarks-filter"
                  placeholder="模糊匹配库存单元备注 如第三方系统箱唛"
                  allowClear
                  value={state.filters.remarks || ''}
                  onChange={(e) => {
                    // 备注里可能有空格，这里不去空格（去了就输不进中间的空格），后端查询时再去首尾空格
                    actions.updateFilters({ remarks: e.target.value || '' });
                  }}
                />
              </Form.Item>
            </Col>
            <Col span={8}>
              <Form.Item>
                <Space>
                  <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                    <span style={{ fontSize: '14px', color: '#666' }}>隐藏0库存记录:</span>
                    <Switch
                      checked={state.filters.hide_zero_stock || false}
                      onChange={(checked) => actions.updateFilters({ hide_zero_stock: checked })}
                      size="small"
                    />
                  </div>
                  <Button 
                    type="primary" 
                    icon={<SearchOutlined />}
                    onClick={() => actions.refresh()}
                  >
                    {t('common.search')}
                  </Button>
                  <Button onClick={handleReset} icon={<ReloadOutlined />}>
                    {t('common.reset')}
                  </Button>
                </Space>
              </Form.Item>
            </Col>
          </Row>
        </Form>

        {/* 数据表格 */}
        <Table
          columns={[
            {
              title: (
                <div style={{ lineHeight: 1.2, textAlign: 'center' }}>
                  <div>系统编码</div>
                  <div style={{ color: '#666' }}>加权库龄</div>
                </div>
              ),
              dataIndex: 'system_barcode',
              key: 'age_avg_days',
              width: 150,
              sorter: { multiple: 3 },
              render: (text: string, record: CustomerInventoryItem) => {
                const ageAvg = (record as any)?.age_avg_days as number | undefined;
                const badgeColor = getAgeBadgeColor(ageAvg);
                return (
                  <Space size={6}>
                    <Text code copyable={{ text }}>{text}</Text>
                    {typeof ageAvg === 'number' ? (
                      <Tooltip title={`加权库龄：${Math.round(ageAvg)} 天`}>
                        <Tag color={badgeColor} style={{ marginInlineStart: 4, backgroundColor: (badgeColor === 'default' ? '#e8f5e9' : undefined), color: (badgeColor === 'default' ? '#1f5130' : undefined) }}>{Math.round(ageAvg)}天</Tag>
                      </Tooltip>
                    ) : null}
                  </Space>
                );
              }
            },
            {
              title: t('inventory.unitType'),
              dataIndex: 'unit_type_display',
              key: 'unit_type_display',
              width: 120,
              render: (text: string, record: CustomerInventoryItem) => {
                const colorMap = {
                  'LIU': 'blue',
                  'BIU': 'green',
                  'PIU': 'orange',
                  'AIU': 'red',
                  'NIU': 'purple'
                };
                return (
                  <Tag color={colorMap[record.unit_type] || 'default'}>
                    {text}
                  </Tag>
                );
              }
            },
            {
              title: t('inventory.project'),
              dataIndex: 'project_info',
              key: 'project_info',
              width: 150,
              render: (projectInfo: any, record: CustomerInventoryItem) => {
                // 优先使用project_info字段
                if (projectInfo && typeof projectInfo === 'object') {
                  return (
                    <div>
                      <div>{projectInfo.name || '未知项目'}</div>
                      <Text type="secondary" style={{ fontSize: '12px', color: '#1890ff', fontWeight: 'bold' }}>
                        {projectInfo.project_code || '无编码'}
                      </Text>
                    </div>
                  );
                }
                // 兼容旧的数据格式
                if (record.project_name || record.project_code) {
                  return (
                    <div>
                      <div>{record.project_name || '未知项目'}</div>
                      <Text type="secondary" style={{ fontSize: '12px', color: '#1890ff', fontWeight: 'bold' }}>
                        {record.project_code || '无编码'}
                      </Text>
                    </div>
                  );
                }
                return <span style={{ color: '#999' }}>-</span>;
              }
            },
            {
              title: t('inventory.sortingGroup'),
              dataIndex: 'sorting_group_info',
              key: 'sorting_group_info',
              width: 150,
              render: (sortingGroupInfo: any, record: CustomerInventoryItem) => {
                // 优先使用sorting_group_info字段
                if (sortingGroupInfo && typeof sortingGroupInfo === 'object') {
                  return (
                    <div>
                      <div>{sortingGroupInfo.name || '未知分拣组'}</div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {sortingGroupInfo.system_code || '无编码'}
                      </Text>
                    </div>
                  );
                }
                // 兼容旧的数据格式
                if (record.sorting_group_name || record.sorting_group_code) {
                  return (
                    <div>
                      <div>{record.sorting_group_name || '未知分拣组'}</div>
                      <Text type="secondary" style={{ fontSize: '12px' }}>
                        {record.sorting_group_code || '无编码'}
                      </Text>
                    </div>
                  );
                }
                return <span style={{ color: '#999' }}>-</span>;
              }
            },
            {
              title: '所含产品信息',
              dataIndex: 'total_products',
              key: 'total_products',
              width: 200,
              align: 'center' as const,
              render: (contentItemsInfo: any, record: CustomerInventoryItem) => {
                if (contentItemsInfo && contentItemsInfo.first_item) {
                  const firstItem = contentItemsInfo.first_item;
                  
                  if (contentItemsInfo.total_count > 1) {
                    // 多个内容项：显示第一个 + 数量提示
                    return (
                      <div 
                        style={{ 
                          textAlign: 'center', 
                          cursor: 'pointer',
                          padding: '8px',
                          border: '1px solid transparent',
                          borderRadius: '4px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#d9d9d9';
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'transparent';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        onClick={() => showContentDetails(contentItemsInfo.all_items, record.system_barcode, record.unit_type_display)}
                        title="点击查看详细信息"
                      >
                        <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                          {firstItem.product_name} × {firstItem.quantity}件
                        </div>
                        <div style={{ fontSize: '12px', color: '#666', marginTop: '4px' }}>
                          +{contentItemsInfo.total_count - 1}条记录
                        </div>
                      </div>
                    );
                  } else {
                    // 只有一个内容项
                    return (
                      <div 
                        style={{ 
                          textAlign: 'center', 
                          cursor: 'pointer',
                          padding: '8px',
                          border: '1px solid transparent',
                          borderRadius: '4px',
                          transition: 'all 0.2s'
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.borderColor = '#d9d9d9';
                          e.currentTarget.style.backgroundColor = '#f5f5f5';
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.borderColor = 'transparent';
                          e.currentTarget.style.backgroundColor = 'transparent';
                        }}
                        onClick={() => showContentDetails(contentItemsInfo.all_items, record.system_barcode, record.unit_type_display)}
                        title="点击查看详细信息"
                      >
                        <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                          {firstItem.product_name} × {firstItem.quantity}件
                        </div>
                      </div>
                    );
                  }
                }
                // 没有内容项
                return <span style={{ color: '#999' }}>-</span>;
              }
            },
            {
              // 与客户后台一致：库存数量分组（总库存/锁定库存/可用库存）
              title: '库存数量',
              key: 'stock_group',
              children: [
                {
                  title: (
                    <div style={{ lineHeight: 1.2 }}>
                      <div>总库存</div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {stockTotalsLoading ? '合计统计中...' : `合计 ${stockTotals.onHand}`}
                      </div>
                    </div>
                  ),
                  key: 'on_hand_qty',
                  width: 90,
                  align: 'center' as const,
                  onCell: () => ({ style: { background: '#f0f5ff' } }),
                  onHeaderCell: () => ({ style: { background: '#f0f5ff' } }),
                  render: (_: any, record: CustomerInventoryItem) => {
                    const v = Number((record as any)?.on_hand_qty);
                    if (!Number.isFinite(v)) return <span style={{ color: '#999' }}>-</span>;
                    const rows = Array.isArray((record as any)?.per_warehouse_quantities)
                      ? ((record as any).per_warehouse_quantities as any[])
                          .filter((x) => Number(x?.on_hand_qty) > 0)
                      : [];
                    const base = (
                      <span style={{
                        color: v > 0 ? '#1677ff' : '#8c8c8c',
                        fontWeight: 'bold',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        display: 'inline-block'
                      }}>
                        {v}
                      </span>
                    );
                    if (!rows || rows.length === 0) return base;
                    return (
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 'bold', marginBottom: 8, textAlign: 'center' }}>库存分布（总库存）</div>
                            {rows.map((x) => (
                              <div key={x.warehouse_id} style={{ marginBottom: 4 }}>
                                {x.warehouse_name}: {x.on_hand_qty}件
                              </div>
                            ))}
                          </div>
                        }
                        placement="top"
                        overlayStyle={{ maxWidth: 240 }}
                      >
                        <span style={{ cursor: 'pointer' }}>{base}</span>
                      </Tooltip>
                    );
                  }
                },
                {
                  title: (
                    <div style={{ lineHeight: 1.2 }}>
                      <div>锁定库存</div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {stockTotalsLoading ? '合计统计中...' : `合计 ${stockTotals.reserved}`}
                      </div>
                    </div>
                  ),
                  key: 'reserved_qty',
                  width: 90,
                  align: 'center' as const,
                  onCell: () => ({ style: { background: '#fff7e6' } }),
                  onHeaderCell: () => ({ style: { background: '#fff7e6' } }),
                  render: (_: any, record: CustomerInventoryItem) => {
                    const v = Number((record as any)?.reserved_qty);
                    if (!Number.isFinite(v)) return <span style={{ color: '#999' }}>-</span>;
                    const rows = Array.isArray((record as any)?.per_warehouse_quantities)
                      ? ((record as any).per_warehouse_quantities as any[])
                          .filter((x) => Number(x?.reserved_qty) > 0)
                      : [];
                    const base = (
                      <span style={{
                        color: v > 0 ? '#fa8c16' : '#8c8c8c',
                        fontWeight: 'bold',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        display: 'inline-block'
                      }}>
                        {v}
                      </span>
                    );
                    if (!rows || rows.length === 0) return base;
                    return (
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 'bold', marginBottom: 8, textAlign: 'center' }}>库存分布（锁定库存）</div>
                            {rows.map((x) => (
                              <div key={x.warehouse_id} style={{ marginBottom: 4 }}>
                                {x.warehouse_name}: {x.reserved_qty}件
                              </div>
                            ))}
                          </div>
                        }
                        placement="top"
                        overlayStyle={{ maxWidth: 240 }}
                      >
                        <span style={{ cursor: 'pointer' }}>{base}</span>
                      </Tooltip>
                    );
                  }
                },
                {
                  title: (
                    <div style={{ lineHeight: 1.2 }}>
                      <div>可用库存</div>
                      <div style={{ fontSize: 12, color: '#666' }}>
                        {stockTotalsLoading ? '合计统计中...' : `合计 ${stockTotals.available}`}
                      </div>
                    </div>
                  ),
                  dataIndex: 'total_quantity',
                  key: 'total_quantity',
                  width: 100,
                  align: 'center' as const,
                  sorter: { multiple: 1 },
                  onCell: () => ({ style: { background: '#f6ffed' } }),
                  onHeaderCell: () => ({ style: { background: '#f6ffed' } }),
                  render: (quantity: number, record: CustomerInventoryItem) => {
                    const warehouseQuantities = record.warehouse_quantities;

                    // 如果没有分仓库信息，只显示总数量
                    if (!warehouseQuantities || Object.keys(warehouseQuantities).length === 0) {
                      return (
                        <span style={{
                          color: quantity > 0 ? 'green' : 'red',
                          fontWeight: 'bold',
                          padding: '4px 8px',
                          borderRadius: '4px',
                          display: 'inline-block'
                        }}>
                          {quantity}
                        </span>
                      );
                    }

                    // 有分仓库信息，显示总数量 + 悬停提示
                    return (
                      <Tooltip
                        title={
                          <div>
                            <div style={{ fontWeight: 'bold', marginBottom: '8px', textAlign: 'center' }}>
                              库存分布（可用）
                            </div>
                            {Object.entries(warehouseQuantities).map(([warehouse, qty]) => (
                              <div key={warehouse} style={{ marginBottom: '4px' }}>
                                {warehouse}: {qty}件
                              </div>
                            ))}
                          </div>
                        }
                        placement="top"
                        overlayStyle={{ maxWidth: '220px' }}
                      >
                        <span
                          style={{
                            color: quantity > 0 ? 'green' : 'red',
                            fontWeight: 'bold',
                            cursor: 'pointer',
                            padding: '4px 8px',
                            borderRadius: '4px',
                            transition: 'all 0.2s ease',
                            display: 'inline-block'
                          }}
                          onMouseEnter={(e) => {
                            e.currentTarget.style.backgroundColor = '#f5f5f5';
                            e.currentTarget.style.boxShadow = '0 2px 8px rgba(0,0,0,0.1)';
                          }}
                          onMouseLeave={(e) => {
                            e.currentTarget.style.backgroundColor = 'transparent';
                            e.currentTarget.style.boxShadow = 'none';
                          }}
                        >
                          {quantity}
                        </span>
                      </Tooltip>
                    );
                  }
                },
              ]
            },
            {
              title: '物流包装',
              dataIndex: 'logistics_package',
              key: 'logistics_package',
              width: 120,
              align: 'center' as const,
              render: (text: string) => {
                const colorMap = {
                  'self_contained': 'green',
                  'no_package': 'orange',
                  'not_applicable': 'default'
                };
                const textMap = {
                  'self_contained': '自带物流包装',
                  'no_package': '无物流包装',
                  'not_applicable': '不适用'
                };
                return (
                  <Tag color={colorMap[text as keyof typeof colorMap] || 'default'}>
                    {textMap[text as keyof typeof textMap] || text}
                  </Tag>
                );
              }
            },
            {
              title: (
                <div style={{ lineHeight: 1.2, textAlign: 'center' }}>
                  <div>总库容体积</div>
                  <div style={{ fontSize: 12, color: '#666' }}>
                    {stockTotalsLoading ? '合计统计中...' : `合计 ${Number(stockTotals.totalVolumeM3 || 0).toFixed(3)} m3`}
                  </div>
                </div>
              ),
              dataIndex: 'calc_total_volume',
              key: 'calc_total_volume',
              width: 120,
              align: 'center' as const,
              sorter: true,
              render: (text: number | null, record: CustomerInventoryItem) => {
                const calcTotalFromApi = (record as any)?.calc_total_volume_m3 as number | undefined;
                let display: string = '-';
                if (typeof calcTotalFromApi === 'number') {
                  display = `${Number(calcTotalFromApi).toFixed(3)} m³`;
                } else if (record.actual_length && record.actual_width && record.actual_height && Number((record as any)?.on_hand_qty || 0) > 0) {
                  const unitVolume = record.actual_length * record.actual_width * record.actual_height; // cm³
                  const totalVolume = unitVolume * Number((record as any)?.on_hand_qty || 0); // cm³
                  display = `${(totalVolume / 1000000).toFixed(3)} m³`;
                }
                const buckets = (record as any)?.age_bucket_volumes || null;
                const copyKey = String((record as any)?.system_barcode || (record as any)?.id || '');
                const copyPayload = buildAgeVolumeCopyText(record);
                const ordered = ['0-30', '31-60', '61-90', '90+'];
                return (
                  <Tooltip
                    color="rgba(0,0,0,0.85)"
                    overlayStyle={{ maxWidth: 340 }}
                    overlayInnerStyle={{ color: '#fff', padding: 12, minWidth: 280 }}
                    placement="top"
                    onVisibleChange={(visible) => {
                      if (!visible) {
                        setCopiedAgeVolumeKey(null);
                      }
                    }}
                    title={
                      <div style={{ position: 'relative' }}>
                        <Button
                          type="text"
                          size="small"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const ok = await copyText(copyPayload);
                            if (ok) {
                              setCopiedAgeVolumeKey(copyKey);
                            }
                          }}
                          style={{
                            position: 'absolute',
                            top: -4,
                            right: -6,
                            color: '#fff',
                            border: '1px solid #1677ff',
                            background: '#1677ff'
                          }}
                        >
                          {copiedAgeVolumeKey === copyKey ? '已复制' : '复制'}
                        </Button>
                        <div style={{ marginBottom: 8, color: '#d9d9d9' }}>
                          库存单元编码：{(record as any)?.system_barcode || (record as any)?.id || '-'}
                        </div>
                        <div style={{ fontWeight: 600, marginBottom: 6 }}>库龄体积分布</div>
                        {!buckets ? (
                          <div style={{ color: '#999' }}>无年龄桶体积分布数据（可能未确认尺寸或暂无在库）</div>
                        ) : (
                          ordered.map((k) => (
                            <div key={k} style={{ display: 'flex', justifyContent: 'space-between' }}>
                              <span style={{ color: '#d9d9d9' }}>{k} 天</span>
                              <span style={{ fontWeight: 600 }}>
                                {typeof (buckets as any)[k] === 'number' ? `${Number((buckets as any)[k]).toFixed(3)} m³` : '-'}
                              </span>
                            </div>
                          ))
                        )}
                      </div>
                    }
                  >
                    <span style={{ cursor: 'pointer' }}>{display}</span>
                  </Tooltip>
                );
              }
            },
            {
              title: t('inventory.weightDimensionsConfirmed'),
              dataIndex: 'weight_dimensions_confirmed',
              key: 'weight_dimensions_confirmed',
              width: 90,
              align: 'center' as const,
              render: (confirmed: boolean, record: CustomerInventoryItem) => {
                const isConfirmed = !!confirmed;
                const rows = isConfirmed
                  ? [
                      { label: '实际长度', value: formatDimValue((record as any)?.actual_length) },
                      { label: '实际宽度', value: formatDimValue((record as any)?.actual_width) },
                      { label: '实际高度', value: formatDimValue((record as any)?.actual_height) },
                      { label: '实际重量', value: formatWeightValue((record as any)?.actual_weight) },
                    ]
                  : [
                      { label: '声明长度', value: formatDimValue((record as any)?.length) },
                      { label: '声明宽度', value: formatDimValue((record as any)?.width) },
                      { label: '声明高度', value: formatDimValue((record as any)?.height) },
                      { label: '声明重量', value: formatWeightValue((record as any)?.weight) },
                    ];
                const copyKey = String((record as any)?.system_barcode || (record as any)?.id || '');
                const copyPayload = buildWeightDimCopyText(record);
                return (
                  <Tooltip
                    color="rgba(0,0,0,0.85)"
                    overlayStyle={{ maxWidth: 340 }}
                    overlayInnerStyle={{ color: '#fff', padding: 12, minWidth: 280 }}
                    placement="top"
                    onVisibleChange={(visible) => {
                      if (!visible) {
                        setCopiedWeightDimKey(null);
                      }
                    }}
                    title={
                      <div style={{ position: 'relative' }}>
                        <Button
                          type="text"
                          size="small"
                          onClick={async (e) => {
                            e.preventDefault();
                            e.stopPropagation();
                            const ok = await copyText(copyPayload);
                            if (ok) {
                              setCopiedWeightDimKey(copyKey);
                            }
                          }}
                          style={{
                            position: 'absolute',
                            top: -4,
                            right: -6,
                            color: '#fff',
                            border: '1px solid #1677ff',
                            background: '#1677ff'
                          }}
                        >
                          {copiedWeightDimKey === copyKey ? '已复制' : '复制'}
                        </Button>
                        <div style={{ marginBottom: 8, color: '#d9d9d9' }}>
                          库存单元编码：{(record as any)?.system_barcode || (record as any)?.id || '-'}
                        </div>
                        <div style={{ marginBottom: 8 }}>
                          <span style={{ marginRight: 8 }}>重量尺寸是否已确认：</span>
                          <Tag color={isConfirmed ? 'success' : 'warning'}>
                            {isConfirmed ? t('common.confirmed') : t('common.unconfirmed')}
                          </Tag>
                        </div>
                        {rows.map((f) => (
                          <div key={f.label} style={{ display: 'flex', justifyContent: 'space-between' }}>
                            <span style={{ color: '#d9d9d9' }}>{f.label}</span>
                            <span style={{ fontWeight: 600 }}>{f.value}</span>
                          </div>
                        ))}
                      </div>
                    }
                  >
                    <span style={{ cursor: 'pointer' }}>
                      <Tag color={isConfirmed ? 'success' : 'warning'}>
                        {isConfirmed ? t('common.confirmed') : t('common.unconfirmed')}
                      </Tag>
                    </span>
                  </Tooltip>
                );
              }
            },
            {
              title: '最近变动记录',
              key: 'recent_operations',
              width: 180,
              align: 'center' as const,
              render: (text: any, record: CustomerInventoryItem) => {
                // 使用 recent_operation 字段获取最近一次变动记录
                if (record.recent_operation) {
                  const { operation_type, change_direction, quantity_change, warehouse, operation_time } = record.recent_operation;
                  
                  // 格式化数量变动显示
                  const quantityDisplay = quantity_change > 0 ? `+${quantity_change}` : quantity_change.toString();
                  
                  // 格式化日期显示
                  const dateDisplay = operation_time ? new Date(operation_time).toLocaleDateString('zh-CN') : '';
                  
                  return (
                    <div style={{ fontSize: '12px', lineHeight: '1.4' }}>
                      <div style={{ fontWeight: 'bold', color: '#1890ff' }}>
                        {operation_type}
                      </div>
                      <div style={{ color: change_direction === '增加' ? '#52c41a' : change_direction === '减少' ? '#ff4d4f' : '#666' }}>
                        {change_direction} {quantityDisplay}
                      </div>
                      <div style={{ color: '#666', fontSize: '11px' }}>
                        {dateDisplay} {warehouse ? `· ${warehouse}` : ''}
                      </div>
                    </div>
                  );
                }
                return '-';
              }
            },
            {
              title: t('common.actions'),
              key: 'actions',
              width: 200,
              fixed: 'right' as const,
              align: 'center' as const,
              render: (_: any, record: CustomerInventoryItem) => (
                <Space size="small" wrap>
                  <Tooltip title={t('common.view')}>
                    <Button
                      type="text"
                      size="small"
                      icon={<EyeOutlined />}
                      onClick={() => handleViewDetail(record)}
                      loading={loading}
                    >{t('common.view')}</Button>
                  </Tooltip>
                  <Tooltip title="查看标签">
                    <Button
                      type="text"
                      size="small"
                      icon={<TagsOutlined />}
                      onClick={async () => {
                        try {
                          const adapter = new InventoryUnitLabelAdapter();
                          const unitType = (record.unit_type || '') as string;
                          const itemsSrc = (record as any)?.total_products?.all_items || [];
                          // LIU也需要传content_items：adapter用它取第一个分拣条码拼进名称里（LIU本身不显示内容物列表，不影响渲染）
                          const contentItems = (unitType === 'BIU' || unitType === 'PIU' || unitType === 'NIU' || unitType === 'LIU')
                            ? (Array.isArray(itemsSrc) ? itemsSrc.map((it: any) => ({ product_name: it.product_name || it.name || '-', quantity: it.quantity || it.qty || 0, sorting_codes: it.sorting_codes })) : [])
                            : [];
                          let resolvedName: string = '';
                          if (unitType === 'AIU') {
                            resolvedName = 'Abnormal Inventory Unit';
                          } else if (unitType === 'LIU') {
                            // 本地字段
                            resolvedName = (record as any)?.name || (record as any)?.label_name || '';
                            // 列表聚合的第一项产品名
                            if (!resolvedName) {
                              const firstItem = Array.isArray(itemsSrc) && itemsSrc.length > 0 ? itemsSrc[0] : null;
                              if (firstItem) {
                                resolvedName = firstItem.product_name || firstItem.name || '';
                              }
                            }
                            // 仍无则通过详情接口补取
                            if (!resolvedName) {
                              try {
                                const detail = await getInventoryUnitDetail(record.id);
                                const d = (detail as any)?.data || detail;
                                const citems = d?.content?.items || d?.content_items || [];
                                if (Array.isArray(citems) && citems.length > 0) {
                                  const it = citems[0];
                                  resolvedName = it?.product_name || it?.product?.name || '';
                                }
                              } catch {}
                            }
                          } else {
                            resolvedName = (record as any)?.system_barcode || '';
                          }
                          const dataForLabel = adapter.transform({
                            id: String(record.id),
                            name: resolvedName,
                            system_code: (record as any)?.system_barcode,
                            text: (record as any)?.system_barcode,
                            unit_type: unitType as any,
                            content_items: contentItems,
                            remarks: (record as any)?.remarks,
                            sorting_group_name: (record as any)?.sorting_group_info?.name
                          });
                          const preview = await inventoryLabelService.generatePreview(dataForLabel);
                          setLabelPreviewUrl(preview.url);
                          setLabelPreviewIsPDF(!!preview.isPDF);
                          setLabelPreviewRecord(record);
                          setLabelPreviewVisible(true);
                        } catch (e) {
                          message.error('生成标签预览失败');
                        }
                      }}
                    >查看标签</Button>
                  </Tooltip>
                </Space>
              )
            }
          ]}
          dataSource={data}
          rowKey="id"
          loading={loading}
          pagination={false}
          scroll={{ x: 1200 }}
          size="middle"
          bordered
          onChange={(pagination, filters, sorter) => {
            console.log('表格排序变化:', sorter);
            if (Array.isArray(sorter) && sorter.length > 0) {
              const sorted = [...sorter]
                .filter(s => s.order)
                .sort((a: any, b: any) => {
                  const ma = typeof a.column?.sorter === 'object' && a.column?.sorter ? (a.column?.sorter as any).multiple || 0 : 0;
                  const mb = typeof b.column?.sorter === 'object' && b.column?.sorter ? (b.column?.sorter as any).multiple || 0 : 0;
                  return mb - ma;
                });
              const ordering = sorted.map((s: any) => {
                const field = (s.columnKey as string) || (s.field as string);
                let backendField = field;
                if (field === 'total_quantity') backendField = 'warehouse_total_quantity';
                if (field === 'calc_total_volume') backendField = 'calc_total_volume';
                if (field === 'age_avg_days') backendField = 'age_avg_days';
                const prefix = s.order === 'descend' ? '-' : '';
                return `${prefix}${backendField}`;
              }).join(',');
              console.log('多列排序->ordering:', ordering);
              actions.updateFilters({ ordering });
            } else if (!Array.isArray(sorter) && sorter && (sorter.field || sorter.columnKey) && sorter.order) {
              const field = sorter.field as string;
              const isDesc = sorter.order === 'descend';
              let backendField = (sorter.columnKey as string) || field;
              if (backendField === 'total_quantity') backendField = 'warehouse_total_quantity';
              if (backendField === 'calc_total_volume') backendField = 'calc_total_volume';
              if (backendField === 'age_avg_days') backendField = 'age_avg_days';
              const ordering = `${isDesc ? '-' : ''}${backendField}`;
              actions.updateFilters({ ordering });
              actions.updateSorting({ field, order: isDesc ? 'desc' : 'asc' });
            } else {
              actions.updateFilters({ ordering: undefined as any });
            }
          }}
        />

        {/* 分页器 */}
        <div style={{ marginTop: 16, display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination
            current={state.pagination.current}
            pageSize={state.pagination.pageSize}
            total={state.pagination.total}
            showSizeChanger
            showQuickJumper
            showTotal={(total) => `共 ${total} 条记录`}
            onChange={(page, pageSize) => {
              actions.updatePagination({ current: page, pageSize });
            }}
          />
        </div>
      </Card>

      {/* 标签预览 */}
      <Modal
        title="库存单元标签预览"
        open={labelPreviewVisible}
        onCancel={() => {
          setLabelPreviewVisible(false);
          if (labelPreviewUrl) { URL.revokeObjectURL(labelPreviewUrl); setLabelPreviewUrl(null); }
          setLabelPreviewRecord(null);
        }}
        footer={[
          <Button key="close" onClick={() => {
            setLabelPreviewVisible(false);
            if (labelPreviewUrl) { URL.revokeObjectURL(labelPreviewUrl); setLabelPreviewUrl(null); }
            setLabelPreviewRecord(null);
          }}>关闭</Button>,
          <Button key="download" type="primary" onClick={async () => {
            try {
              const rec = labelPreviewRecord;
              if (!rec) return;
              const adapter = new InventoryUnitLabelAdapter();
              const unitType = (rec.unit_type || '') as string;
              const itemsSrc = (rec as any)?.total_products?.all_items || [];
              // LIU也需要传content_items：adapter用它取第一个分拣条码拼进名称里（LIU本身不显示内容物列表，不影响渲染）
              const contentItems = (unitType === 'BIU' || unitType === 'PIU' || unitType === 'NIU' || unitType === 'LIU')
                ? (Array.isArray(itemsSrc) ? itemsSrc.map((it: any) => ({ product_name: it.product_name || it.name || '-', quantity: it.quantity || it.qty || 0, sorting_codes: it.sorting_codes })) : [])
                : [];
              let resolvedName: string = '';
              if (unitType === 'AIU') {
                resolvedName = 'Abnormal Inventory Unit';
              } else if (unitType === 'LIU') {
                resolvedName = (rec as any)?.name || (rec as any)?.label_name || '';
                if (!resolvedName) {
                  const firstItem = Array.isArray(itemsSrc) && itemsSrc.length > 0 ? itemsSrc[0] : null;
                  if (firstItem) resolvedName = firstItem.product_name || firstItem.name || '';
                }
                if (!resolvedName) {
                  try {
                    const detail = await getInventoryUnitDetail(rec.id);
                    const d = (detail as any)?.data || detail;
                    const citems = d?.content?.items || d?.content_items || [];
                    if (Array.isArray(citems) && citems.length > 0) {
                      const it = citems[0];
                      resolvedName = it?.product_name || it?.product?.name || '';
                    }
                  } catch {}
                }
              } else {
                resolvedName = (rec as any)?.system_barcode || '';
              }
              const dataForPDF = adapter.transform({
                id: String(rec.id),
                name: resolvedName,
                system_code: (rec as any)?.system_barcode,
                text: (rec as any)?.system_barcode,
                unit_type: unitType as any,
                content_items: contentItems,
                remarks: (rec as any)?.remarks,
                sorting_group_name: (rec as any)?.sorting_group_info?.name
              });
              const blob = await inventoryLabelService.generatePDF(dataForPDF);
              const url = URL.createObjectURL(blob);
              const a = document.createElement('a');
              a.href = url;
              a.download = `${(rec as any)?.system_barcode || 'label'}.pdf`;
              document.body.appendChild(a);
              a.click();
              document.body.removeChild(a);
              URL.revokeObjectURL(url);
            } catch {
              message.error('下载失败');
            }
          }}>下载 PDF</Button>
        ]}
        width={600}
      >
        {labelPreviewUrl ? (
          labelPreviewIsPDF ? (
            <iframe
              title="label-preview-pdf"
              src={labelPreviewUrl}
              style={{ width: '100%', height: 520, border: 0 }}
            />
          ) : (
            <div style={{ textAlign: 'center' }}>
              <img src={labelPreviewUrl} alt="label-preview" style={{ maxWidth: '100%' }} />
            </div>
          )
        ) : (
          <div style={{ textAlign: 'center', color: '#999' }}>无预览</div>
        )}
      </Modal>

      {/* 数据排序面板 */}
      <Modal
        title="数据排序"
        open={sortModalVisible}
        onCancel={() => setSortModalVisible(false)}
        onOk={applyMultiSort}
        okText="应用"
        cancelText="取消"
      >
        <div className="space-y-3">
          <div style={{ marginBottom: 8, color: '#666' }}>最多支持二级排序规则。</div>
          {sortRules.map((rule, idx) => (
            <div key={idx} style={{ display: 'flex', gap: 8, alignItems: 'center', marginBottom: 8 }}>
              <span style={{ width: 70 }}>优先级{idx + 1}</span>
              <Select
                placeholder="选择字段"
                value={rule.field || undefined}
                style={{ width: 220 }}
                options={sortFieldOptions}
                onChange={(val) => {
                  const next = [...sortRules];
                  next[idx] = { ...next[idx], field: val } as any;
                  setSortRules(next);
                }}
              />
              <Select
                placeholder="顺序"
                value={rule.order}
                style={{ width: 120 }}
                options={[{ label: '升序', value: 'asc' }, { label: '降序', value: 'desc' }]}
                onChange={(val) => {
                  const next = [...sortRules];
                  next[idx] = { ...next[idx], order: val } as any;
                  setSortRules(next);
                }}
              />
              <Button type="link" danger onClick={() => setSortRules(prev => prev.filter((_, i) => i !== idx))}>删除</Button>
            </div>
          ))}
          {sortRules.length < 2 && (
            <Button type="dashed" onClick={() => setSortRules(prev => [...prev, { field: '', order: 'desc' }])}>
              添加排序规则
            </Button>
          )}
          <div style={{ marginTop: 8 }}>
            <Button onClick={resetMultiSort}>清除排序</Button>
          </div>
        </div>
      </Modal>

      {/* 内容项详细信息弹窗 */}
      <Modal
        title={
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', paddingRight: 36 }}>
            <span>库存单元内容项详细信息</span>
            {contentDetailsUnitCode ? (
              <span style={{ margin: '0 12px' }}>
                {contentDetailsUnitTypeText ? <Text strong style={{ fontSize: 18, marginRight: 8 }}>{contentDetailsUnitTypeText}</Text> : null}
                <Text code style={{ fontSize: 18, fontWeight: 600 }}>{contentDetailsUnitCode}</Text>
              </span>
            ) : null}
            <Button
              type="primary"
              size="small"
              onClick={async () => {
                const ok = await copyText(buildContentDetailsCopyText(contentDetailsData));
                if (ok) setContentDetailsCopied(true);
              }}
            >
              {contentDetailsCopied ? '已复制' : '复制'}
            </Button>
          </div>
        }
        open={contentDetailsVisible}
        onCancel={() => { setContentDetailsVisible(false); setContentDetailsCopied(false); setContentDetailsUnitCode(''); }}
        footer={[
          <Button key="close" onClick={() => { setContentDetailsVisible(false); setContentDetailsCopied(false); setContentDetailsUnitCode(''); }}>
            关闭
          </Button>
        ]}
        width={800}
      >
        <div>
          {contentDetailsData.map((item, index) => (
            <div 
              key={index} 
              style={{ 
                border: '1px solid #d9d9d9', 
                borderRadius: '6px', 
                padding: '16px', 
                marginBottom: '16px',
                backgroundColor: '#fafafa'
              }}
            >
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '12px' }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 12 }}>
                  {item.photo ? (
                    <Image
                      src={getAttachmentUrl({ url: item.photo })}
                      alt={item.product_name}
                      width={56}
                      height={56}
                      style={{ objectFit: 'cover', borderRadius: 6 }}
                    />
                  ) : null}
                  <Text strong style={{ fontSize: '16px', color: '#1890ff' }}>
                    {item.product_name}
                  </Text>
                </div>
                <Tag color="blue" style={{ fontSize: '14px' }}>
                  数量: {item.quantity}件
                </Tag>
              </div>
              
              <Row gutter={16}>
                <Col span={12}>
                  <div style={{ marginBottom: '8px' }}>
                    <Text type="secondary">客户自定义编码:</Text>
                    <div style={{ marginTop: '4px', fontWeight: '500' }}>
                      {item.product_code}
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div style={{ marginBottom: '8px' }}>
                    <Text type="secondary">系统编码:</Text>
                    <div style={{ marginTop: '4px', fontWeight: '500' }}>
                      {item.system_code}
                    </div>
                  </div>
                </Col>
              </Row>
              
              {item.sorting_codes && item.sorting_codes.length > 0 && (
                <div style={{ marginTop: '12px' }}>
                  <Text type="secondary">分拣条码:</Text>
                  <div style={{ marginTop: '8px' }}>
                    {item.sorting_codes.map((code: any, codeIndex: number) => (
                      <Tag 
                        key={codeIndex} 
                        color="green" 
                        style={{ marginRight: '8px', marginBottom: '4px' }}
                      >
                        {code.code}
                        {code.description && (
                          <span style={{ marginLeft: '4px', fontSize: '12px' }}>
                            ({code.description})
                          </span>
                        )}
                      </Tag>
                    ))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>
      </Modal>

      {/* 下载字段选择弹窗 */}
      <DownloadFieldsModal
        visible={downloadModalVisible}
        onCancel={() => setDownloadModalVisible(false)}
        onConfirm={handleConfirmDownload}
        loading={downloading}
      />
    </div>
  );
};

// 下载字段选择弹窗组件
const DownloadFieldsModal: React.FC<{
  visible: boolean;
  onCancel: () => void;
  onConfirm: (selectedFields: string[]) => void;
  loading?: boolean;
}> = ({ visible, onCancel, onConfirm, loading }) => {
  const [selectedFields, setSelectedFields] = useState<string[]>([]);
  const { exportHiddenFields } = usePermissions();
  const exportHidden = exportHiddenFields('inventory.InventoryUnit');
  const isFieldHiddenForExport = useCallback((fieldKey: string): boolean => {
    const mapping: Record<string, string[]> = {
      // “所属客户”字段对应的敏感字段键
      customer_name: ['sorting_group__project_inventory_config__project__customer__username'],
    };
    const related = mapping[fieldKey] || [];
    if (!Array.isArray(exportHidden) || exportHidden.length === 0) return false;
    return related.some(k => exportHidden.includes(k));
  }, [exportHidden]);
  
  // 字段配置定义（基础集合，不含策略过滤） - 使用useMemo避免重复创建
  const DOWNLOAD_FIELDS_BASE = useMemo(() => [
    {
      group: '基本信息',
      fields: [
        { key: 'customer_name', label: '所属客户' },
        { key: 'system_barcode', label: '系统编码' },
        { key: 'unit_type_display', label: '库存单元类型' },
        { key: 'project_name', label: '项目名称' },
        { key: 'project_code', label: '项目编码' },
        { key: 'sorting_group_name', label: '分拣组名称' },
        { key: 'sorting_group_code', label: '分拣组编码' },
        { key: 'source', label: '来源类型' },
        { key: 'on_hand_qty', label: '总库存' },
        { key: 'reserved_qty', label: '锁定库存' },
        { key: 'available_qty', label: '可用库存' },
        { key: 'warehouse_quantities', label: '可用库存分布' }
      ]
    },
    {
      group: '所含产品信息',
      fields: [
        { key: 'product_system_code', label: '产品系统编码' },
        { key: 'product_name', label: '产品名称' },
        { key: 'product_custom_code', label: '产品客户自定义编码' },
        { key: 'product_sorting_barcode', label: '产品分拣条码' },
        { key: 'product_quantity', label: '所含产品数量' },
        { key: 'product_remarks', label: '备注' }
      ]
    },
    {
      group: '包装信息',
      fields: [
        { key: 'declared_actual_dimensions', label: '声明/实际尺寸' },
        { key: 'declared_actual_weight', label: '声明/实际重量' },
        { key: 'unit_volume', label: '声明/实际单位体积' },
        { key: 'weight_dimensions_confirmed', label: '重量尺寸确认状态' },
        { key: 'logistics_package', label: '物流包装' }
      ]
    },
    {
      group: '业务信息',
      fields: [
        { key: 'recent_operation', label: '最近变动记录' },
        { key: 'calc_total_volume', label: '总库容体积(CBM)' },
        { key: 'storage_age_summary', label: '库龄汇总' }
      ]
    }
  ], []);

  // 按导出敏感策略过滤后的字段集合
  const DOWNLOAD_FIELDS = useMemo(() => (
    DOWNLOAD_FIELDS_BASE
      .map(group => ({
        ...group,
        fields: group.fields.filter(field => !isFieldHiddenForExport(field.key))
      }))
      .filter(group => group.fields.length > 0)
  ), [DOWNLOAD_FIELDS_BASE, isFieldHiddenForExport]);
  
  // 计算全选状态 - 使用useMemo避免重复计算
  const allFields = useMemo(() => 
    DOWNLOAD_FIELDS.flatMap(group => 
      group.fields.map(field => field.key)
    ), []
  );
  
  const isAllSelected = useMemo(() => 
    selectedFields.length === allFields.length, 
    [selectedFields.length, allFields.length]
  );
  
  const isIndeterminate = useMemo(() => 
    selectedFields.length > 0 && selectedFields.length < allFields.length,
    [selectedFields.length, allFields.length]
  );

  // 调试信息
  console.log('🔍 计算状态:', {
    selectedFieldsLength: selectedFields.length,
    allFieldsLength: allFields.length,
    isAllSelected,
    isIndeterminate,
    selectedFields,
    allFields
  });
  
  // 当模态框打开时，默认全选所有字段
  useEffect(() => {
    if (visible && allFields.length > 0) {
      setSelectedFields(allFields);
      console.log('🚀 模态框打开，初始化全选状态，字段数量:', allFields.length);
      console.log('🚀 初始字段列表:', allFields);
    }
  }, [visible, allFields]);
  
  // 当模态框关闭时，清理状态
  useEffect(() => {
    if (!visible) {
      // 延迟清理，确保关闭动画完成
      const timer = setTimeout(() => {
        setSelectedFields([]);
        console.log('🧹 模态框关闭，清理选择状态');
      }, 300);
      
      return () => clearTimeout(timer);
    }
  }, [visible]);

  // 监控selectedFields变化
  useEffect(() => {
    console.log('🔍 selectedFields状态变化:', {
      count: selectedFields.length,
      fields: selectedFields,
      timestamp: new Date().toISOString()
    });
  }, [selectedFields]);

  // 监控allFields变化
  useEffect(() => {
    console.log('🔍 allFields状态变化:', {
      count: allFields.length,
      fields: allFields,
      timestamp: new Date().toISOString()
    });
  }, [allFields]);
  
  // 全选/取消全选逻辑
  const handleSelectAll = useCallback((e: any) => {
    console.log('🔍 全选变化:', { checked: e.target.checked, allFields });
    
    if (e.target.checked) {
      setSelectedFields(allFields);
      console.log('✅ 全选所有字段');
    } else {
      setSelectedFields([]);
      console.log('❌ 取消全选所有字段');
    }
  }, [allFields]);
  
  // 单个字段选择逻辑
  const handleFieldChange = useCallback((fieldKey: string, checked: boolean) => {
    console.log('🔍 字段选择变化:', { fieldKey, checked });
    
    if (checked) {
      setSelectedFields(prev => {
        const newSelected = [...prev, fieldKey];
        console.log('✅ 添加字段后:', newSelected);
        return newSelected;
      });
    } else {
      setSelectedFields(prev => {
        const newSelected = prev.filter(key => key !== fieldKey);
        console.log('❌ 移除字段后:', newSelected);
        return newSelected;
      });
    }
  }, []);
  
  // 分组选择逻辑
  const handleGroupChange = useCallback((groupFields: string[], checked: boolean) => {
    console.log('🔍 分组选择变化:', { groupFields, checked });
    
    if (checked) {
      // 添加分组字段（去重）
      setSelectedFields(prev => {
        const combined = [...prev, ...groupFields];
        const newSelected = combined.filter((item, index) => combined.indexOf(item) === index);
        console.log('✅ 添加分组字段后:', newSelected);
        return newSelected;
      });
    } else {
      // 移除分组字段
      setSelectedFields(prev => {
        const newSelected = prev.filter(key => !groupFields.includes(key));
        console.log('✅ 移除分组字段后:', newSelected);
        return newSelected;
      });
    }
  }, []);
  
  // 重置选择，恢复全选状态
  const handleReset = () => {
    setSelectedFields(allFields);
    console.log('🔄 重置为全选状态，字段数量:', allFields.length);
  };
  
  // 处理取消操作，清理状态
  const handleCancel = () => {
    setSelectedFields([]);
    console.log('❌ 取消操作，清理选择状态');
    onCancel();
  };
  
  // 确认下载，直接传递当前选择
  const handleConfirm = () => {
    if (selectedFields.length === 0) {
      message.warning('请至少选择一个字段');
      return;
    }
    
    console.log('✅ 确认下载，选择的字段:', selectedFields);
    console.log('✅ 字段数量:', selectedFields.length);
    
    // 直接传递当前选择的字段，不设置任何外部状态
    onConfirm(selectedFields);
    
    // 确认后自动清理状态
    setSelectedFields([]);
  };
  
  return (
    <Modal
      title="选择下载字段"
      open={visible}
      onCancel={handleCancel}
      footer={[
        <Button key="reset" onClick={handleReset}>
          重置
        </Button>,
        <Button key="cancel" onClick={handleCancel}>
          取消
        </Button>,
        <Button 
          key="confirm" 
          type="primary" 
          loading={loading}
          onClick={handleConfirm}
          disabled={selectedFields.length === 0}
        >
          确认下载 ({selectedFields.length} 个字段)
        </Button>
      ]}
      width={700}
      destroyOnClose
    >
      <div className="space-y-4">
        {/* 全选控制 */}
        <div className="border-b pb-3">
          {(() => {
            console.log('🔍 渲染全选Checkbox:', { 
              isAllSelected, 
              isIndeterminate, 
              selectedFields,
              allFields 
            });
            return (
              <Checkbox
                indeterminate={isIndeterminate}
                checked={isAllSelected}
                onChange={(e) => {
                  console.log('🔍 全选Checkbox onChange:', { checked: e.target.checked });
                  handleSelectAll(e);
                }}
              >
                <span className="font-medium">全选/取消全选</span>
              </Checkbox>
            );
          })()}
        </div>

        {/* 字段分组 */}
        {DOWNLOAD_FIELDS.map((group, groupIndex) => {
          const groupFields = group.fields.map(field => field.key);
          const groupSelectedCount = groupFields.filter(field => 
            selectedFields.includes(field)
          ).length;
          const isGroupSelected = groupSelectedCount === groupFields.length;
          const isGroupIndeterminate = groupSelectedCount > 0 && groupSelectedCount < groupFields.length;

          return (
            <div key={groupIndex} className="space-y-2">
              <div className="flex items-center space-x-2">
                {(() => {
                  console.log(`🔍 渲染分组 ${group.group}:`, { 
                    isGroupSelected, 
                    isGroupIndeterminate, 
                    groupSelectedCount,
                    groupFields,
                    selectedFields 
                  });
                  return (
                    <Checkbox
                      indeterminate={isGroupIndeterminate}
                      checked={isGroupSelected}
                      onChange={(e) => {
                        console.log('🔍 分组Checkbox onChange:', { group: group.group, checked: e.target.checked, groupFields });
                        handleGroupChange(groupFields, e.target.checked);
                      }}
                    >
                      <span className="font-medium text-blue-600">
                        {group.group} ({groupSelectedCount}/{groupFields.length})
                      </span>
                    </Checkbox>
                  );
                })()}
              </div>
              
              <div className="ml-6 grid grid-cols-2 gap-2">
                {group.fields.map((field) => {
                  const isChecked = selectedFields.includes(field.key);
                  console.log(`🔍 渲染字段 ${field.key}:`, { isChecked, selectedFields });
                  
                  return (
                    <Checkbox
                      key={field.key}
                      checked={isChecked}
                      onChange={(e) => {
                        console.log('🔍 Checkbox onChange:', { 
                          field: field.key, 
                          checked: e.target.checked,
                          beforeChange: selectedFields,
                          isChecked
                        });
                        handleFieldChange(field.key, e.target.checked);
                      }}
                    >
                      {field.label}
                    </Checkbox>
                  );
                })}
              </div>
              
              {groupIndex < DOWNLOAD_FIELDS.length - 1 && <Divider />}
            </div>
          );
        })}

        {/* 提示信息 */}
        <div className="text-sm text-gray-500 bg-gray-50 p-3 rounded">
          💡 提示：选择的字段越多，文件越大，下载时间越长。建议只选择需要的字段。
        </div>
      </div>
    </Modal>
  );
};

export default CurrentInventory;
